// DO NOT EDIT -- generated from fs/api/proactiveinsights/proactiveinsights.proto

import { AlertConfig } from './alertconfig.proto';
import { DxIdVersion } from './dxobjects.proto';
import * as funnelmetrics from '../metrics/funnelmetrics';
import * as metrics from '../metrics';
import * as protobuf from '../../../google/protobuf';
import * as visualizationsettings from '../../services/search-settings/visualizationsettings';

export interface Insight {
  insightId: string;
  kind: InsightKind;
  computationKind: ComputationKind;
  /**
   * The hash of the computation used to compute the data for this Insight. Useful in case we want to group insights
   * by computation versus by DxVersion.
   */
  computationHash: string;
  dxObject: DxIdVersion | null;
  /** insight pertains to this window of time */
  computedAt: protobuf.timestamp | null;
  start: protobuf.timestamp | null;
  end: protobuf.timestamp | null;
  /** assumed that the creator / updater will always be FullStory */
  createdAt: protobuf.timestamp | null;
  updatedAt: protobuf.timestamp | null;
  alertMetadata: AlertMetadata | null;
  trend?: TrendDetail | null;
  trendGroup?: TrendGroupDetail | null;
  absoluteThreshold?: AbsoluteThresholdDetail | null;
  absoluteChangeThreshold?: AbsoluteChangeThresholdDetail | null;
  relativeChangeThreshold?: RelativeChangeThresholdDetail | null;
}

export namespace Insight {
  export enum detail {
    trend = 10,
    trendGroup = 11,
    absoluteThreshold = 13,
    absoluteChangeThreshold = 14,
    relativeChangeThreshold = 15
  }

  export function detail_oneof(m: Insight): { opt: detail.absoluteChangeThreshold, val: AbsoluteChangeThresholdDetail } | { opt: detail.absoluteThreshold, val: AbsoluteThresholdDetail } | { opt: detail.relativeChangeThreshold, val: RelativeChangeThresholdDetail } | { opt: detail.trend, val: TrendDetail } | { opt: detail.trendGroup, val: TrendGroupDetail } | null {
    if (m.trend != null) { return { opt: detail.trend, val: m.trend } }
    if (m.trendGroup != null) { return { opt: detail.trendGroup, val: m.trendGroup } }
    if (m.absoluteThreshold != null) { return { opt: detail.absoluteThreshold, val: m.absoluteThreshold } }
    if (m.absoluteChangeThreshold != null) { return { opt: detail.absoluteChangeThreshold, val: m.absoluteChangeThreshold } }
    if (m.relativeChangeThreshold != null) { return { opt: detail.relativeChangeThreshold, val: m.relativeChangeThreshold } }
    return null;
  }
}

/**
 * The DX object data ingested by the Insights Engine.
 * This data object is passed to insights generators.
 */
export interface DXInputData {
  /**
   * Multiple DX cards can have the same computation
   * The request hash will help us identify the duplicates
   * It is the hash of the alexa compute card (metric/funnel…) requests
   */
  requestHash: string;
  /** determines the underlying data source */
  dxIdVersion: DxIdVersion | null;
  /**
   * computation kind determines the number of computations required
   * to generate all the data for the DX card
   */
  computationKind: ComputationKind;
  /**
   * complete card data contains the computation(s) required to render the card
   * the request hash in the ID is computed from the complete card data computation(s)
   * It also captures the data tables(s) for each computation(s)
   * This data along with the settings is sufficient to render the card
   * without needing any solr queries
   */
  data: DXCardData | null;
  /** the time at which the data was computed */
  dataComputedAt: protobuf.timestamp | null;
  /** timezone in which the data was computed. UTC when empty */
  timezone: string;
}

export interface PieConsumptionData {
  dxIdVersion: DxIdVersion | null;
  /** the total 'grouped' requests being sent. */
  totalRequests: number;
  browserClient?: BrowserClient | null;
  alertPollingClient?: AlertPollingClient | null;
  storeOnly: boolean;
}

export namespace PieConsumptionData {
  /**
   * ProducerData represents metadata from the producer who is sending the request (in alexa and pie).
   * This metadata can be related to the dx object itself. For example, a BrowserClient will send
   * whether the Dx Object in question is saved or not.
   * The metadata can also be related to how the client itself is processing the data. For example,
   * an AlertPollingClient will pass along the alert configs such that PIE knows that it should
   * generate insights using those configs.
   * 
   * PIE will use this data to adjust how it stores Dx Object data and generates insights.
   */
  export enum producer_data {
    browserClient = 3,
    alertPollingClient = 4
  }

  /**
   * ProducerData represents metadata from the producer who is sending the request (in alexa and pie).
   * This metadata can be related to the dx object itself. For example, a BrowserClient will send
   * whether the Dx Object in question is saved or not.
   * The metadata can also be related to how the client itself is processing the data. For example,
   * an AlertPollingClient will pass along the alert configs such that PIE knows that it should
   * generate insights using those configs.
   * 
   * PIE will use this data to adjust how it stores Dx Object data and generates insights.
   */
  export function producer_data_oneof(m: PieConsumptionData): { opt: producer_data.alertPollingClient, val: AlertPollingClient } | { opt: producer_data.browserClient, val: BrowserClient } | null {
    if (m.browserClient != null) { return { opt: producer_data.browserClient, val: m.browserClient } }
    if (m.alertPollingClient != null) { return { opt: producer_data.alertPollingClient, val: m.alertPollingClient } }
    return null;
  }
}

/**
 * Represents configs
 * defined by the user to specify what data to query from Solr
 * and what algorithm to use for determining if an alert should be fired.
 */
export interface AlertPollingClient {
  configs: AlertConfig[];
}

/**
 * Represents metadata pertaining to a Dx Object Alexa request originating from the browser.
 * This could either be about the Dx Object itself (e.g. is_saved) or configurations for how FSTA
 * sends data.
 */
export interface BrowserClient {
  /** a boolean for if this is a saved dxId object */
  isSaved: boolean;
}

export interface DXCardData {
  /**
   * computation and corresponding index correlated data
   * there will be multiple computations and corresponding
   * data results only in the case of:
   * Case 1: A multi segment request on a trend and funnel metrics, one table for each segment
   * Case 2: A compare past period request on a trend, dimensionality and funnel metrics
   *         resulting in two data tables, one for current and past period respectively.
   */
  cardComputations: DXDataComputation[];
  dataResults: DXDataResult[];
}

export interface DXDataComputation {
  metricComputation?: metrics.Computation | null;
  funnelComputation?: funnelmetrics.Computation | null;
  /** Should always be set, will be false for most requests */
  isPast: boolean;
  /**
   * the time range comparison if the computation is compare to past
   * required for compare to past because the previous computation will be filtered out when we hash
   * we we need to be able to tell the difference between different comparison ranges
   */
  timeRangeComparison: visualizationsettings.TimeRangeComparison | null;
}

export namespace DXDataComputation {
  export enum computation {
    metricComputation = 1,
    funnelComputation = 2
  }

  export function computation_oneof(m: DXDataComputation): { opt: computation.funnelComputation, val: funnelmetrics.Computation } | { opt: computation.metricComputation, val: metrics.Computation } | null {
    if (m.metricComputation != null) { return { opt: computation.metricComputation, val: m.metricComputation } }
    if (m.funnelComputation != null) { return { opt: computation.funnelComputation, val: m.funnelComputation } }
    return null;
  }
}

export interface DXDataResult {
  metricsDataTable?: metrics.DataTable | null;
  /**
   * Each funnel metric computation produces a FunnelDataTables result, which is an array of FunnelDataTable.
   * Each FunnelDataTable contains the results for one step in the funnel.
   */
  funnelDataTables?: funnelmetrics.FunnelDataTables | null;
}

export namespace DXDataResult {
  export enum result {
    metricsDataTable = 1,
    /**
     * Each funnel metric computation produces a FunnelDataTables result, which is an array of FunnelDataTable.
     * Each FunnelDataTable contains the results for one step in the funnel.
     */
    funnelDataTables = 2
  }

  export function result_oneof(m: DXDataResult): { opt: result.funnelDataTables, val: funnelmetrics.FunnelDataTables } | { opt: result.metricsDataTable, val: metrics.DataTable } | null {
    if (m.metricsDataTable != null) { return { opt: result.metricsDataTable, val: m.metricsDataTable } }
    if (m.funnelDataTables != null) { return { opt: result.funnelDataTables, val: m.funnelDataTables } }
    return null;
  }
}

export enum ComputationKind {
  COMPUTATION_KIND_INVALID = 0,
  COMPUTATION_KIND_SINGLE_NUMBER = 1,
  COMPUTATION_KIND_SINGLE_NUMBER_COMPARE_PAST = 2,
  COMPUTATION_KIND_TREND = 3,
  COMPUTATION_KIND_TREND_COMPARE_PAST = 4,
  COMPUTATION_KIND_TREND_MULTI_SEGMENT = 5,
  COMPUTATION_KIND_TREND_DIMENSIONALITY = 6,
  COMPUTATION_KIND_TREND_DIMENSIONALITY_COMPARE_PAST = 13,
  COMPUTATION_KIND_DIMENSIONALITY = 7,
  COMPUTATION_KIND_DIMENSIONALITY_COMPARE_PAST = 8,
  COMPUTATION_KIND_FUNNEL_TREND = 9
}

/** NB: InsightKind is persisted to a Datastore field and CANNOT be reordered without due diligence. */
export enum InsightKind {
  INSIGHT_KIND_INVALID = 0,
  INSIGHT_KIND_WEEK_OVER_WEEK = 1,
  INSIGHT_KIND_MONTH_OVER_MONTH = 3,
  INSIGHT_KIND_ABSOLUTE_THRESHOLD = 4,
  INSIGHT_KIND_ABSOLUTE_CHANGE_THRESHOLD = 5,
  INSIGHT_KIND_RELATIVE_CHANGE_THRESHOLD = 6
}

/**
 * Direction for insights specify how the insight was generated and may not
 * be described with `DIRECTION_INCREASE_OR_DECREASE`.
 */
export enum Direction {
  DIRECTION_INVALID = 0,
  DIRECTION_INCREASING = 1,
  DIRECTION_DECREASING = 2
}

export interface TrendDetail {
  /** The complete trend data that was used to generate the insight */
  data: DXDataResult | null;
  /** the direction of change */
  direction: Direction;
  /** periods are strictly sorted in ascending or chronological order */
  periodicStats: PeriodicStats[];
  /**
   * defined as (new_stat / old_stat) for both mean and var based insights
   * so this is the ratio of the new stat to old stat
   */
  ratioChange: number;
  /**
   * Steps of the funnel selected for trend, by index. Will not be set for insights generated on trended metrics.
   * For trended funnels, TrendSteps is always set on the insight object to specify how the insight was generated.
   * The trended funnel definition may have TrendSteps be nil, which implies all steps, but here these are set explicitly.
   */
  trendSteps: visualizationsettings.TrendSteps | null;
}

export interface PeriodicStats {
  /** start time of a period */
  startDate: protobuf.timestamp | null;
  /** end time of a period */
  endDate: protobuf.timestamp | null;
  /** mean values of each time period */
  mean?: number;
  /** std values of each time period */
  variability?: TrendVariability | null;
}

export namespace PeriodicStats {
  export enum stats {
    /** mean values of each time period */
    mean = 3,
    /** std values of each time period */
    variability = 4
  }

  export function stats_oneof(m: PeriodicStats): { opt: stats.mean, val: number } | { opt: stats.variability, val: TrendVariability } | null {
    if (m.mean != null) { return { opt: stats.mean, val: m.mean } }
    if (m.variability != null) { return { opt: stats.variability, val: m.variability } }
    return null;
  }
}

export interface TrendVariability {
  /**
   * upper and lower std deviation distances from the mean of the period
   * i.e. mean +- std
   */
  stdUpper: number;
  stdLower: number;
}

export interface TrendGroupDetail {
  /** the week over week aggregate trend group data */
  periodicTables: TrendGroupData[];
  /** stats for the change in the top valued group over the weeks */
  topValueChange: TrendGroupFeatureStats | null;
  /** change in the gap between the groups above and below the knee */
  gapChange: TrendGroupFeatureStats | null;
  /** determines the features detected in order to generate the insight */
  featuresDetected: TrendGroupDetail.FeatureUsed;
}

export namespace TrendGroupDetail {
  export enum FeatureUsed {
    FEATURES_USED_INVALID = 0,
    FEATURES_USED_TOP_VALUE_ONLY = 1,
    FEATURES_USED_GAP_CHANGE_ONLY = 2,
    FEATURES_USED_TOP_VALUE_AND_GAP_CHANGE = 3
  }
}

export interface TrendGroupData {
  /**
   * aggregation of across groups in a week
   * this table is sorted by value
   */
  aggregateTable: DXDataResult | null;
  /** the knee or first big drop in the group metric value */
  kneeIndex: number;
  /** top label and value for this period */
  top: TopGroupValue | null;
  /**
   * the gap between the groups above the knee and below the knee
   * defined as aggregate_table[knee-1][value] - aggregate_table[knee][value]
   */
  gap: number;
}

/**
 * Trigger type provides more context regarding how the alert is crossing the threshold (already over and still over
 * or just crossed).
 */
export enum TriggerType {
  /** This is the default for backwards compatibility */
  TRIGGER_TYPE_CROSSED = 0,
  TRIGGER_TYPE_REMAINING = 1,
  /** Not used yet but may be used in the future to alert when the value goes below the threshold */
  TRIGGER_TYPE_RESOLVED = 2,
  TRIGGER_TYPE_INVALID = 3
}

/** TopGroupValue is used for absolute threshold insights */
export interface TopGroupValue {
  /** The type of alert trigger this was (just crossed the threshold, still exceeding it, etc) */
  triggerType: TriggerType;
  /**
   * in some case like PageScreenResolution, there could be more
   * than one dimension that maps to a group
   */
  groups: metrics.DataRowGroup[];
  /** value of the metric at the current data point that triggered the insight */
  value: number;
}

export interface TopGroupChangeValue {
  /**
   * in some case like PageScreenResolution, there could be more
   * than one dimension that maps to a group
   */
  groups: metrics.DataRowGroup[];
  previousValue: number;
  currentValue: number;
  /** Direction of the data during the time period that triggered the insight */
  direction: Direction;
  /** calculated relative change or absolute change, used for notifications */
  calculatedChange: number;
}

export interface TrendGroupFeatureStats {
  /** determines the direction of the change */
  direction: Direction;
  /** the change as the fold factor (new_stat / old_stat) */
  ratioChange: number;
  /** the mean of the previous week (not including the latest week) */
  previousMean: number;
}

export interface AlertMetadata {
  alertConfigId: string;
  alertConfigVersion: number;
  alertConfigHash: string;
}

export interface AbsoluteThresholdDetail {
  /**
   * groups are used to keep track of which groups and what value have crossed the alert threshold
   * Depending on the metric type, groups may contain a dimension identifier or be empty.
   * Trend/Multi-Segment/Trend Compare Past: No groups listed within DataRowGroup, but will contain the value that crossed threshold
   * Group By: Dimension identifier will be within groups (ex; "Chrome", or Page definition Ids such as 1234).
   */
  groups: TopGroupValue[];
}

export interface AbsoluteChangeThresholdDetail {
  /**
   * groups are used to keep track of which groups have crossed the alert threshold and associated data
   * Depending on the metric type, groups may contain a dimension identifier or be empty.
   * Trend/Multi-Segment: No groups listed within DataRowGroup, but will contain the absolute value that crossed threshold
   */
  groups: TopGroupChangeValue[];
}

export interface RelativeChangeThresholdDetail {
  /**
   * groups are used to keep track of which groups have crossed the alert threshold and associated data
   * Depending on the metric type, groups may contain a dimension identifier or be empty.
   * Trend/Multi-Segment: No groups listed within DataRowGroup, but will contain the percent change value that crossed threshold
   */
  groups: TopGroupChangeValue[];
}
